import { useFormik } from "formik";
import _ from "lodash";

import theme from "theme";
import { colors } from "theme/palette";

import Button from "components/Button";
import Dialog, { DialogContent, DialogTitle } from "components/Dialog";
import Stack from "components/Stack";
import Text from "components/Text";
import TextField from "components/TextField";

import { VALIDATION_SCHEMA } from "./consts";

export interface ISnapshotCreateDialog {
  onClose: () => void;
  onUpdate: () => void;
  open: boolean;
  navigateToPDFRenderer: ({
    snapshotName,
  }: {
    snapshotName: string;
  }) => void | Promise<void>;
  hideDescription: boolean;
}

const SnapshotCreateDialog = ({
  onClose,
  open,
  navigateToPDFRenderer,
  hideDescription,
}: ISnapshotCreateDialog) => {
  const formik = useFormik({
    initialValues: { name: "" },
    validationSchema: VALIDATION_SCHEMA,
    onSubmit: (values) => {
      navigateToPDFRenderer({ snapshotName: values.name });
    },
  });
  const handleClose = () => {
    onClose();
    formik.resetForm();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Text
          variant="h2"
          fontWeight={700}
          padding={2}
          sx={{ background: colors.blue10, borderRadius: 2 }}
        >
          + Create new snapshot
        </Text>
      </DialogTitle>
      <DialogContent
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
          marginTop: 0.5,
        }}
      >
        <Text variant="text3" color={colors.gray80}>
          Create a saved snapshot of this checklist to preserve a
          ‘frozen-in-time’ version of the state it is currently in! It captures
          all the collected documents and filled out information, which can be
          viewed or downloaded later.
          {!hideDescription &&
            " It will display statuses, but doesn’t capture additional features like tasks and messages."}
        </Text>

        <Stack gap={0.5}>
          <Stack>
            <Text variant="text3" fontWeight={500}>
              Snapshot name
            </Text>
            <Text variant="text4" color={colors.gray40}>
              Name the snapshot you are about to create!
            </Text>
          </Stack>
          <Stack gap={4}>
            <TextField
              {...formik.getFieldProps("name")}
              fullWidth
              slotProps={{
                formHelperText: { sx: { margin: "inherit" } },
              }}
              helperText={
                formik.touched.name &&
                formik.errors.name && (
                  <Text
                    variant="text4"
                    sx={{ backgroundColor: colors.white }}
                    padding={theme.spacing(0.5, 1.5)}
                  >
                    {formik.errors.name}
                  </Text>
                )
              }
              type="text"
              placeholder="Snapshot name"
              variant="outlined"
              sx={{ backgroundColor: colors.blue10 }}
              error={Boolean(formik.touched.name && formik.errors.name)}
            />
            <Stack direction="row" gap={1} alignSelf="flex-end">
              <Button variant="text" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                onClick={formik.submitForm}
                disabled={
                  _.isEmpty(formik.values.name) || _.isNil(formik.values.name)
                }
              >
                Create snapshot
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default SnapshotCreateDialog;
